import type { GuideAnnotation, GuideAnnotationIntent } from '@erp/rpc';
import type { Stroke } from 'src/components/common/Annotator';

/**
 * Converts between the Annotator's stage-pixel strokes and the guide's image-normalized
 * annotations (AI-488 follow-on). The Annotator stays pixel-based; this is the one place the
 * coordinate spaces meet. `rect` is the rendered size of the image the marks were drawn over.
 */
export interface StageRect {
  width: number;
  height: number;
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

function norm(p: { x: number; y: number }, rect: StageRect) {
  return {
    x: clamp01(rect.width > 0 ? p.x / rect.width : 0),
    y: clamp01(rect.height > 0 ? p.y / rect.height : 0),
  };
}

function denorm(p: { x: number; y: number }, rect: StageRect) {
  return { x: p.x * rect.width, y: p.y * rect.height };
}

/** Tool → annotation kind. The Annotator has no circle tool; circles only come from imports. */
const KIND_BY_TOOL = {
  pen: 'freehand',
  arrow: 'arrow',
  rect: 'rect',
  point: 'marker',
  text: 'text',
} as const;

/** Text marks are commentary; every other mark defaults to `intent` (the dialog's choice). */
export function toGuideAnnotations(
  strokes: Stroke[],
  rect: StageRect,
  intent: GuideAnnotationIntent,
  existing: GuideAnnotation[] = [],
): GuideAnnotation[] {
  const existingById = new Map(
    existing.map((annotation) => [annotation.id, annotation]),
  );
  const out: GuideAnnotation[] = [];
  for (const stroke of strokes) {
    const original = stroke.sourceAnnotationId
      ? existingById.get(stroke.sourceAnnotationId)
      : undefined;
    // The current Annotator can remove seeded strokes but cannot reshape them. Keep every retained
    // seeded mark byte-for-byte so a no-op save cannot rewrite circle geometry or its metadata.
    if (original) {
      out.push(original);
      continue;
    }
    const kind = KIND_BY_TOOL[stroke.tool];
    let points: { x: number; y: number }[] = [];
    if (stroke.tool === 'pen' && stroke.path?.length) {
      points = stroke.path.map((p) => norm(p, rect));
    } else if (
      (stroke.tool === 'arrow' || stroke.tool === 'rect') &&
      stroke.from &&
      stroke.to
    ) {
      points = [norm(stroke.from, rect), norm(stroke.to, rect)];
    } else if (
      (stroke.tool === 'point' || stroke.tool === 'text') &&
      stroke.x != null &&
      stroke.y != null
    ) {
      points = [norm({ x: stroke.x, y: stroke.y }, rect)];
    }
    if (points.length === 0) continue;
    if (stroke.tool === 'text' && !stroke.text?.trim()) continue;
    out.push({
      id: String(stroke.id),
      intent: stroke.tool === 'text' ? 'note' : intent,
      kind,
      points,
      ...(stroke.tool === 'text' ? { label: stroke.text?.trim() } : {}),
      ...(stroke.tool === 'point' && stroke.n != null ? { n: stroke.n } : {}),
      color: stroke.color,
      provenance: 'human',
    });
  }
  return out;
}

const DEFAULT_COLOR = 'oklch(0.62 0.2 25)';

/** Stored annotations → editable strokes. A `circle` (import-only) becomes a rect around it. */
export function toAnnotatorStrokes(
  annotations: GuideAnnotation[],
  rect: StageRect,
): Stroke[] {
  const out: Stroke[] = [];
  annotations.forEach((a, index) => {
    const id = Number.isFinite(Number(a.id)) ? Number(a.id) : index + 1;
    const color = a.color ?? DEFAULT_COLOR;
    const [first, second] = a.points;
    switch (a.kind) {
      case 'freehand':
        out.push({
          id,
          sourceAnnotationId: a.id,
          tool: 'pen',
          color,
          path: a.points.map((p) => denorm(p, rect)),
        });
        break;
      case 'arrow':
      case 'rect':
        out.push({
          id,
          sourceAnnotationId: a.id,
          tool: a.kind,
          color,
          from: denorm(first, rect),
          to: denorm(second ?? first, rect),
        });
        break;
      case 'circle': {
        const c = denorm(first, rect);
        const e = second ? denorm(second, rect) : null;
        const r = e ? Math.hypot(e.x - c.x, e.y - c.y) : rect.width * 0.02;
        out.push({
          id,
          sourceAnnotationId: a.id,
          tool: 'rect',
          color,
          from: { x: c.x - r, y: c.y - r },
          to: { x: c.x + r, y: c.y + r },
        });
        break;
      }
      case 'marker': {
        const p = denorm(first, rect);
        out.push({
          id,
          sourceAnnotationId: a.id,
          tool: 'point',
          color,
          x: p.x,
          y: p.y,
          n: a.n,
        });
        break;
      }
      case 'text': {
        const p = denorm(first, rect);
        out.push({
          id,
          sourceAnnotationId: a.id,
          tool: 'text',
          color,
          x: p.x,
          y: p.y,
          text: a.label,
        });
        break;
      }
    }
  });
  return out;
}
